import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { SoldProductGraphByDayFilter } from "../fragments.dispatches/SoldProductGraphByDayFilter";

export function SoldProductGraphByDayPage() {
    const barChartRef = useRef(null);
    const [soldProducts, setSoldProducts] = useState([]);

    useEffect(() => {
        const barChart = new Chart(barChartRef.current, {
            type: "bar",
            data: {
                labels:   soldProducts.map((p) => p.productName),
                datasets: [{
                    label:           "Cantidad Vendida",
                    data:            soldProducts.map((p) => p.quantitySold),
                    backgroundColor: "#07bc0c",
                    borderRadius:    4,
                    borderSkipped:   false,
                }],
            },
            options: {
                indexAxis:  "y",
                responsive: true,
                plugins: {
                    legend: { position: "top" },
                    title:  { display: true, text: "Productos más vendidos", font: { size: 14 } },
                    tooltip: {
                        callbacks: { label: (ctx) => ` ${ctx.raw} unidades` },
                    },
                },
                scales: {
                    x: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.05)" } },
                    y: { grid: { display: false } },
                },
            },
        });
        return () => barChart.destroy();
    }, [soldProducts]);

    // Summary stats
    const totalUnits  = soldProducts.reduce((s, p) => s + (p.quantitySold ?? 0), 0);
    const topProduct  = soldProducts.length > 0
        ? soldProducts.reduce((a, b) => (a.quantitySold > b.quantitySold ? a : b))
        : null;

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Ventas de productos
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Reportes</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Productos vendidos por día</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

                        {/* Filter + summary */}
                        <div className="lg:col-span-4 space-y-4">
                            <SoldProductGraphByDayFilter setSoldProducts={setSoldProducts} />

                            {/* Summary cards */}
                            {soldProducts.length > 0 && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3 text-center">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Productos</p>
                                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{soldProducts.length}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3 text-center">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Total unidades</p>
                                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{totalUnits}</p>
                                    </div>
                                    {topProduct && (
                                        <div className="col-span-2 rounded-xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wide mb-0.5">
                                                <i className="fas fa-trophy mr-1"></i>Más vendido
                                            </p>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                {topProduct.productName}
                                            </p>
                                            <p className="text-xs text-emerald-700 dark:text-emerald-300 font-bold">
                                                {topProduct.quantitySold} unidades
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Chart */}
                        <div className="lg:col-span-8">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <i className="fas fa-chart-bar text-emerald-500"></i>
                                        Productos más vendidos
                                    </h2>
                                    {soldProducts.length > 0 && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {soldProducts.length} productos · {totalUnits} unidades en total
                                        </p>
                                    )}
                                </div>

                                {soldProducts.length > 0 ? (
                                    <div className="p-5">
                                        <canvas ref={barChartRef} />
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16">
                                        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                                            <i className="fas fa-chart-bar text-gray-400 dark:text-gray-500 text-2xl"></i>
                                        </div>
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin datos</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            Seleccione los filtros y presione Buscar.
                                        </p>
                                        {/* Canvas must always be in DOM for Chart.js ref to work */}
                                        <canvas ref={barChartRef} className="hidden" />
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
}
